import { useState, type ChangeEvent, type ReactNode } from 'react'
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogContent, FormControlLabel, FormHelperText, Stack, TextField, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useController, useForm, type Control } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCadastrarNoCashbackMutation, type CadastroNoCashback, type Usuario } from '../api'
import { cpfValido } from '../lib/cpf'
import { soDigitos } from '../lib/digitos'
import { formatarBRL, formatarCpf } from '../lib/formato'
import { useAcaoUnica } from '../lib/use-acao-unica'
import { CabecalhoModal } from './cabecalho-modal'

// A mesma regra do backend: de 10 dígitos (fixo com DDD) a 13 (celular com o 55 na frente).
function telefoneValido(telefone: string): boolean {
  const digitos = soDigitos(telefone)
  return digitos.length >= 10 && digitos.length <= 13
}

/**
 * As mesmas regras do backend (`backend/src/rotas/me-cashback.ts`). O formulário barra antes
 * porque a mensagem de validação de lá vem com o nome do campo na frente ("senha: ...").
 */
const esquemaDoCadastro = z.object({
  nome: z.string().trim().min(1, 'Informe seu nome').max(200, 'Use até 200 letras no nome'),
  cpf: z.string().min(1, 'Informe seu CPF').refine(cpfValido, 'CPF inválido. Confira os números.'),
  telefone: z.string().max(30, 'Informe o celular com DDD').refine(telefoneValido, 'Informe o celular com DDD'),
  senha: z.string().min(6, 'A senha precisa ter 6 caracteres ou mais').max(200, 'Use até 200 caracteres na senha'),
  aceiteTermos: z.boolean().refine((aceitou) => aceitou, 'Marque que você aceita os termos para continuar.'),
})

type DadosDoFormulario = z.infer<typeof esquemaDoCadastro>

interface ErroDoCadastro {
  mensagem: string
  /** A loja desligou o uso de cashback: tentar de novo não adianta, então o formulário sai da tela. */
  lojaNaoAceita: boolean
}

const TEXTO_CONFIRA_OS_DADOS = 'Não conseguimos concluir o cadastro. Confira os dados e tente de novo.'
const TEXTO_TENTE_DE_NOVO = 'Não conseguimos concluir o cadastro agora. Tente de novo em instantes.'
const TEXTO_LOJA_NAO_ACEITA = 'Esta loja não está aceitando cashback nos pedidos agora.'

interface DialogCashbackProps {
  /** Perfil do cliente logado: o nome, o CPF e o celular já vêm preenchidos com o que ele tem. */
  usuario: Pick<Usuario, 'nome' | 'cpf' | 'telefone'>
  aoFechar: () => void
}

/**
 * Cadastro do cliente no cashback, sem sair do cardápio. Aberto por "Minha conta" e pelo checkout.
 * Monte só enquanto estiver aberto: o formulário nasce do perfil a cada abertura.
 *
 * A senha fica só no formulário: não vai para o `localStorage`, para a store nem para o log.
 */
export function DialogCashback({ usuario, aoFechar }: DialogCashbackProps): JSX.Element {
  const [cadastrar] = useCadastrarNoCashbackMutation()
  const [cadastroFeito, setCadastroFeito] = useState<CadastroNoCashback | null>(null)
  const [erro, setErro] = useState<ErroDoCadastro | null>(null)

  const { control, handleSubmit } = useForm<DadosDoFormulario>({
    resolver: zodResolver(esquemaDoCadastro),
    defaultValues: {
      nome: usuario.nome,
      cpf: formatarCpf(usuario.cpf ?? ''),
      telefone: usuario.telefone ?? '',
      senha: '',
      aceiteTermos: false,
    },
  })

  async function enviarCadastro(dados: DadosDoFormulario): Promise<void> {
    setErro(null)
    try {
      const cadastro = await cadastrar({
        nome: dados.nome,
        cpf: soDigitos(dados.cpf),
        telefone: soDigitos(dados.telefone),
        senha: dados.senha,
        // O formulário só chega aqui com o aceite marcado.
        aceiteTermos: true,
      }).unwrap()
      setCadastroFeito(cadastro)
    } catch (falha) {
      setErro(erroParaOCliente(falha))
    }
  }

  // Toque repetido no botão não vira dois cadastros: o segundo responderia "você já tinha conta".
  const [enviarUmaVez, enviando] = useAcaoUnica(enviarCadastro)

  function fechar(): void {
    // Fechar no meio do envio deixaria o cliente sem saber se o cadastro foi feito.
    if (enviando) return
    aoFechar()
  }

  if (cadastroFeito) {
    return (
      <Moldura aoFechar={fechar}>
        <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center' }}>
          <CheckCircleOutlineIcon color="success" fontSize="large" />
          <Typography>{mensagemDoResultado(cadastroFeito)}</Typography>
          <Button variant="contained" size="large" fullWidth disableElevation onClick={fechar}>
            Continuar
          </Button>
        </Stack>
      </Moldura>
    )
  }

  if (erro?.lojaNaoAceita) {
    return (
      <Moldura aoFechar={fechar}>
        <Stack spacing={2}>
          <Alert severity="warning">{erro.mensagem}</Alert>
          <Button variant="contained" size="large" fullWidth disableElevation onClick={fechar}>
            Fechar
          </Button>
        </Stack>
      </Moldura>
    )
  }

  return (
    <Moldura aoFechar={fechar}>
      <Stack component="form" spacing={2} onSubmit={handleSubmit(enviarUmaVez)} noValidate>
        <Typography variant="body2" color="text.secondary">
          Cadastre-se para usar seu cashback como desconto nos pedidos desta loja. Use o mesmo CPF e celular que você
          informa nas compras: é por eles que achamos o que você já ganhou.
        </Typography>
        <CampoDoCadastro controle={control} campo="nome" rotulo="Nome" autoCompletar="name" />
        <CampoDoCadastro
          controle={control}
          campo="cpf"
          rotulo="CPF"
          exemplo="000.000.000-00"
          formatar={formatarCpf}
          tecladoNumerico
          autoCompletar="off"
        />
        <CampoDoCadastro
          controle={control}
          campo="telefone"
          rotulo="Celular com DDD"
          exemplo="(00) 00000-0000"
          tipo="tel"
          autoCompletar="tel"
        />
        <CampoDoCadastro
          controle={control}
          campo="senha"
          rotulo="Crie uma senha para o cashback"
          ajuda="Senha nova, de 6 caracteres ou mais, só para o site do cashback. No cardápio você continua entrando com o código do e-mail."
          tipo="password"
          autoCompletar="new-password"
        />
        <AceiteDosTermos controle={control} />
        {erro && <Alert severity="error">{erro.mensagem}</Alert>}
        <Button
          type="submit"
          variant="contained"
          size="large"
          disableElevation
          disabled={enviando}
          startIcon={enviando ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {enviando ? 'Cadastrando...' : 'Cadastrar'}
        </Button>
      </Stack>
    </Moldura>
  )
}

function Moldura({ aoFechar, children }: { aoFechar: () => void; children: ReactNode }): JSX.Element {
  return (
    <Dialog open onClose={aoFechar} fullWidth maxWidth="xs" scroll="paper">
      <CabecalhoModal titulo="Cadastro no cashback" aoFechar={aoFechar} />
      <DialogContent sx={{ pt: 2, pb: 3 }}>{children}</DialogContent>
    </Dialog>
  )
}

interface CampoDoCadastroProps {
  controle: Control<DadosDoFormulario>
  campo: 'nome' | 'cpf' | 'telefone' | 'senha'
  rotulo: string
  /** Formato esperado, mostrado dentro do campo vazio. */
  exemplo?: string
  /** Texto de apoio embaixo do campo. Dá lugar à mensagem de erro quando o campo está errado. */
  ajuda?: string
  /** Máscara aplicada ao que o cliente digita (a do CPF). */
  formatar?: (digitado: string) => string
  tipo?: 'text' | 'tel' | 'password'
  /** Abre o teclado só de números no celular. */
  tecladoNumerico?: boolean
  autoCompletar: string
}

function CampoDoCadastro(props: CampoDoCadastroProps): JSX.Element {
  const { controle, campo, rotulo, exemplo, ajuda, formatar, tipo = 'text', tecladoNumerico = false, autoCompletar } = props
  const { field, fieldState } = useController({ name: campo, control: controle })
  const mensagemDeErro = fieldState.error?.message

  function aoDigitar(evento: ChangeEvent<HTMLInputElement>): void {
    const digitado = evento.target.value
    field.onChange(formatar ? formatar(digitado) : digitado)
  }

  return (
    <TextField
      label={rotulo}
      placeholder={exemplo}
      type={tipo}
      name={field.name}
      value={field.value}
      onChange={aoDigitar}
      onBlur={field.onBlur}
      inputRef={field.ref}
      error={!!mensagemDeErro}
      helperText={mensagemDeErro ?? ajuda}
      autoComplete={autoCompletar}
      fullWidth
      slotProps={{ htmlInput: { inputMode: tecladoNumerico ? 'numeric' : undefined } }}
    />
  )
}

function AceiteDosTermos({ controle }: { controle: Control<DadosDoFormulario> }): JSX.Element {
  const { field, fieldState } = useController({ name: 'aceiteTermos', control: controle })
  const mensagemDeErro = fieldState.error?.message

  return (
    <Box>
      {/* Sem link de propósito: as páginas de termos e de política de privacidade ainda não existem. */}
      <FormControlLabel
        control={
          <Checkbox
            name={field.name}
            checked={field.value}
            onChange={(_evento, marcado) => field.onChange(marcado)}
            onBlur={field.onBlur}
            inputRef={field.ref}
          />
        }
        label={<Typography variant="body2">Li e aceito os termos do programa e a política de privacidade</Typography>}
      />
      {mensagemDeErro && <FormHelperText error>{mensagemDeErro}</FormHelperText>}
    </Box>
  )
}

function mensagemDoResultado(cadastro: CadastroNoCashback): string {
  if (cadastro.resultado === 'CONTA_CRIADA') return 'Pronto! Sua conta de cashback foi criada.'

  if (cadastro.resultado === 'CONTA_ASSUMIDA') {
    // Sem a consulta do saldo (cashback fora do ar logo depois do cadastro), "R$ 0,00" seria um saldo inventado.
    if (!cadastro.disponivel) return 'Achamos seu cashback nesta loja.'
    return `Achamos seu cashback: ${formatarBRL(cadastro.saldoDisponivel)} nesta loja.`
  }

  // A senha digitada foi descartada: a conta já tinha uma, e o cliente precisa saber qual das duas vale.
  if (cadastro.resultado === 'CONTA_JA_EXISTIA') {
    return 'Você já tinha conta no cashback. Seu saldo já aparece aqui. Sua senha do cashback continua a de antes.'
  }

  return 'Pronto! Seu cadastro no cashback foi concluído.'
}

/** O erro se trata pelo `codigo`. Só as mensagens escritas para o cliente ler são mostradas como vieram. */
function erroParaOCliente(falha: unknown): ErroDoCadastro {
  const { codigo, mensagem } = lerErroDaApi(falha)

  if (codigo === 'CASHBACK_USO_DESLIGADO') {
    return { mensagem: mensagem ?? TEXTO_LOJA_NAO_ACEITA, lojaNaoAceita: true }
  }

  const temMensagemParaOCliente = codigo === 'CASHBACK_INDISPONIVEL' || codigo === 'MUITAS_REQUISICOES'
  if (temMensagemParaOCliente && mensagem) return { mensagem, lojaNaoAceita: false }

  // Não deveria acontecer (o formulário valida antes), e a mensagem do backend traz o nome do campo na frente.
  if (codigo === 'VALIDACAO') return { mensagem: TEXTO_CONFIRA_OS_DADOS, lojaNaoAceita: false }

  return { mensagem: TEXTO_TENTE_DE_NOVO, lojaNaoAceita: false }
}

/** `codigo` e `mensagem` do corpo de erro da API. Sem corpo (a rede caiu, por exemplo), os dois vêm null. */
function lerErroDaApi(falha: unknown): { codigo: string | null; mensagem: string | null } {
  const semCorpo = { codigo: null, mensagem: null }
  if (typeof falha !== 'object' || falha === null || !('data' in falha)) return semCorpo

  const corpo = falha.data
  if (typeof corpo !== 'object' || corpo === null) return semCorpo

  const { codigo, mensagem } = corpo as { codigo?: unknown; mensagem?: unknown }
  return {
    codigo: typeof codigo === 'string' ? codigo : null,
    mensagem: typeof mensagem === 'string' ? mensagem : null,
  }
}
